import { X, Clock, Loader2, CheckCircle2, XCircle, PauseCircle, Ban, type LucideIcon } from "lucide-react";
import type ExecutionStatus from "../../models/ExecutionStatus";

interface ExecutionPanelProps {
  status: ExecutionStatus | null;
  polling: boolean;
  onClose: () => void;
}

const STATUS_META: Record<string, { label: string; icon: LucideIcon; color: string }> = {
  Pending: {
    label: "Pendiente",
    icon: Clock,
    color: "text-amber-600 bg-amber-500/10 border-amber-500/20",
  },
  Running: {
    label: "Ejecutando",
    icon: Loader2,
    color: "text-blue-600 bg-blue-500/10 border-blue-500/20",
  },
  Completed: {
    label: "Completado",
    icon: CheckCircle2,
    color: "text-green-600 bg-green-500/10 border-green-500/20",
  },
  Failed: {
    label: "Fallido",
    icon: XCircle,
    color: "text-red-600 bg-red-500/10 border-red-500/20",
  },
  Suspended: {
    label: "Suspendido",
    icon: PauseCircle,
    color: "text-orange-600 bg-orange-500/10 border-orange-500/20",
  },
  Cancelled: {
    label: "Cancelado",
    icon: Ban,
    color: "text-gray-600 bg-gray-500/10 border-gray-500/20",
  },
};

export default function ExecutionPanel({ status, polling, onClose }: ExecutionPanelProps) {
  if (!status) return null;

  const meta = STATUS_META[status.status] ?? STATUS_META.Pending;
  const Icon = meta.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-md" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white/90 backdrop-blur-2xl rounded-3xl p-6 shadow-2xl z-10 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-foreground">Ejecución</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-black/5 transition-colors"
            aria-label="Cerrar"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-bold ${meta.color}`}>
          <Icon className={`size-4 ${polling ? "animate-spin" : ""}`} />
          {meta.label}
        </div>

        <div className="space-y-1 text-sm text-foreground/60">
          <p>
            <span className="font-semibold text-foreground/80">ID:</span> {status.executionId}
          </p>
          <p>
            <span className="font-semibold text-foreground/80">Encolado:</span>{" "}
            {new Date(status.enqueuedAtUtc).toLocaleString()}
          </p>
          {status.startedAtUtc && (
            <p>
              <span className="font-semibold text-foreground/80">Iniciado:</span>{" "}
              {new Date(status.startedAtUtc).toLocaleString()}
            </p>
          )}
          {status.completedAtUtc && (
            <p>
              <span className="font-semibold text-foreground/80">Completado:</span>{" "}
              {new Date(status.completedAtUtc).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
